import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Dropdown } from 'components/v2/Dropdown'
import { FormattedDateTime } from '@10xLabs/react-web/components/FormattedDateTime'
import { StyledLink } from 'components/v2/Link'
import List from 'components/v2/List'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { CampaignStatus } from 'features/Loyalty/components/LoyaltyCampaignStatus'
import { LoyaltyCampaignActions } from 'features/Loyalty/views/LoyaltyCampaigns/List/Actions'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { ILoyaltyCampaignsListProps } from './types'

const DurationWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  > div {
    margin-left: 2px;
  }
`

export function LoyaltyCampaignsList({ campaigns }: ILoyaltyCampaignsListProps): ReactElement {
  const intl = useIntl()
  const login = useLogin()

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canWrite(FEATURES.LOYALTY_CAMPAIGN, login.accessToken.policies) && <Th />}
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.name" defaultMessage="Campaign Name" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.rules" defaultMessage="Rules" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.description" defaultMessage="Description" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.duration" defaultMessage="Duration" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.company" defaultMessage="Company" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.line" defaultMessage="Lines" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {campaigns.map((loyaltyCampaign) => {
            return (
              <Tr key={loyaltyCampaign?.id}>
                {canWrite(FEATURES.LOYALTY_CAMPAIGN, login.accessToken.policies) && (
                  <Td>
                    <LoyaltyCampaignActions loyaltyCampaign={loyaltyCampaign} />
                  </Td>
                )}
                <Td>
                  <CampaignStatus status={loyaltyCampaign.status} />
                </Td>
                <Td>{loyaltyCampaign?.name}</Td>
                <Td>
                  {loyaltyCampaign?.rules.length > 0 ? (
                    <StyledLink as={NavLink} to={`/loyalty/campaigns/${loyaltyCampaign.id}/rules`}>
                      {loyaltyCampaign.rules.length}
                    </StyledLink>
                  ) : (
                    0
                  )}
                </Td>
                <Td>{loyaltyCampaign?.description}</Td>
                <Td>
                  <DurationWrapper>
                    <FormattedMessage id="loyaltyCampaigns.start.date" defaultMessage="Starts:"></FormattedMessage>

                    <FormattedDateTime date={loyaltyCampaign?.startAt} intl={intl} />
                  </DurationWrapper>

                  <DurationWrapper>
                    <FormattedMessage id="loyaltyCampaigns.start.end" defaultMessage="Ends:"></FormattedMessage>

                    <FormattedDateTime date={loyaltyCampaign?.endAt} intl={intl} />
                  </DurationWrapper>
                </Td>
                <Td>{loyaltyCampaign?.company.name}</Td>
                <Td>
                  {loyaltyCampaign?.companyLines.length > 0 ? (
                    <Dropdown toggle={<StyledLink>{loyaltyCampaign?.companyLines.length}</StyledLink>} noPadding>
                      <List.Container>
                        {loyaltyCampaign?.companyLines.map(({ name, id }) => {
                          return <List.Item key={id}>{name}</List.Item>
                        })}
                      </List.Container>
                    </Dropdown>
                  ) : (
                    0
                  )}
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
